/**
 * Telemetry Mappers
 *
 * Bi-directional conversion between domain models and DTOs
 */

import { RawTelemetryData } from '../../domain/telemetry/raw/RawTelemetryData'
import {
  TelemetryDto,
  LatLngDto,
  GyroReading,
  AccelerationReading,
} from '../apiTypes'

/**
 * Convert RawTelemetryData to TelemetryDto for API calls
 *
 * This is the CORRECT way to save telemetry to the backend.
 * Raw data preserves the original device readings without interpolation or calculation.
 *
 * @param source - Raw telemetry data from the device
 * @returns TelemetryDto ready for API submission
 */
export function rawTelemetryToDto(source: RawTelemetryData): TelemetryDto {
  const telemetryDto: TelemetryDto = {
    ...(source.device && {
      device: {
        name: source.device.deviceName,
        version: source.device.deviceVersion,
      },
    }),
  }

  // Map GPS readings (raw data from device)
  if (source.gps && source.gps.length > 0) {
    telemetryDto.gps = {
      positions: source.gps.map(
        (reading): LatLngDto => ({
          latitude: reading.latitude,
          longitude: reading.longitude,
          altitude: reading.altitude,
          speed: { kph: reading.speedMs ? reading.speedMs * 3.6 : 0 }, // Convert m/s to km/h
          time: {
            synchronizationTime: undefined, // Raw data doesn't have synchronization time yet
            recordedTime: reading.timestamp,
          },
          distanceTravelled: reading.distanceMeters,
        })
      ),
    }
  }

  // Map gyroscope readings (raw sensor data)
  if (source.gyroscope && source.gyroscope.length > 0) {
    telemetryDto.gyroscope = {
      unit: source.gyroscope[0].unit ?? 'rad/s',
      direction: {},
      gyroReading: source.gyroscope.map(
        (reading): GyroReading => ({
          x: reading.x,
          y: reading.y,
          z: reading.z,
          time: {
            synchronizationTime: undefined,
            recordedTime: reading.timestamp,
          },
        })
      ),
    }
  }

  // Map accelerometer readings (raw sensor data)
  if (source.accelerometer && source.accelerometer.length > 0) {
    telemetryDto.accelerometer = {
      unit: source.accelerometer[0].unit ?? 'g',
      direction: {},
      accelerationReading: source.accelerometer.map(
        (reading): AccelerationReading => ({
          x: reading.x,
          y: reading.y,
          z: reading.z,
          time: {
            synchronizationTime: undefined,
            recordedTime: reading.timestamp,
          },
        })
      ),
    }
  }

  return telemetryDto
}

/**
 * Convert TelemetryDto to RawTelemetryData for processing
 *
 * This is used when loading telemetry from the backend for analysis.
 * Converts API response format back to domain model for processing pipeline.
 *
 * @param telemetryDto - Telemetry data from API response
 * @param fallbackStartTime - Optional fallback start time if no GPS data available
 * @returns RawTelemetryData ready for processing
 */
export function telemetryDtoToRaw(
  telemetryDto: TelemetryDto,
  fallbackStartTime?: Date
): RawTelemetryData {
  // Convert GPS positions
  const gps =
    telemetryDto.gps?.positions.map((pos) => ({
      latitude: pos.latitude,
      longitude: pos.longitude,
      altitude: pos.altitude,
      speedMs: pos.speed?.kph ? pos.speed.kph / 3.6 : 0, // Convert km/h to m/s
      timestamp: new Date(pos.time.recordedTime),
      // Only include distanceMeters if it's actually provided (not null/undefined)
      // Otherwise let processRawTelemetry calculate it from GPS coordinates
      ...(pos.distanceTravelled != null && {
        distanceMeters: pos.distanceTravelled,
      }),
    })) ?? []

  // Convert gyroscope readings
  const gyroscope = telemetryDto.gyroscope?.gyroReading?.map((reading) => ({
    x: reading.x,
    y: reading.y,
    z: reading.z,
    timestamp: new Date(reading.time.recordedTime),
    synchronizationTime: reading.time.synchronizationTime
      ? new Date(reading.time.synchronizationTime).getTime()
      : undefined,
    unit: (telemetryDto.gyroscope?.unit === 'deg/s' ? 'deg/s' : 'rad/s') as
      | 'rad/s'
      | 'deg/s',
  }))

  // Convert accelerometer readings
  const accelerometer = telemetryDto.accelerometer?.accelerationReading?.map(
    (reading) => ({
      x: reading.x,
      y: reading.y,
      z: reading.z,
      timestamp: new Date(reading.time.recordedTime),
      cts: reading.time.synchronizationTime
        ? new Date(reading.time.synchronizationTime).getTime()
        : undefined,
      unit: (telemetryDto.accelerometer?.unit === 'm/s²' ? 'm/s²' : 'g') as
        | 'g'
        | 'm/s²',
    })
  )

  const recordingStartTime =
    gps[0]?.timestamp ?? fallbackStartTime ?? new Date()

  return {
    gps,
    gyroscope,
    accelerometer,
    recordingStartTime,
    device: telemetryDto.device
      ? {
          deviceName: telemetryDto.device.name,
          deviceVersion: telemetryDto.device.version,
        }
      : undefined,
  }
}
